import {
  Row,
  Column,
  Img,
  Tailwind,
  Container,
  Body,
  Html,
  Link,
} from '@react-email/components'
import { type TFunction } from 'i18next'
import { type ReactNode } from 'react'
import { FooterText } from '#app/components/email/ui/footer-text.tsx'
import {
  physicalAddress,
  socialLinks,
  socialIconSize,
  type SocialLinkData,
} from '#app/utils/global.ts'

export type EmailType = 'sign-up' | 'reset-password'

const fullPxWidth = 600

function Gutter({ width }: { width: number }) {
  return <Column className={`w-[${width}px]`} />
}

function SocialLink({ name, href, imgPath }: SocialLinkData) {
  return (
    <div>
      <span className="hidden">{name}: </span>
      <Link href={href}>
        <Img alt={name} src={imgPath} />
      </Link>
    </div>
  )
}

function SocialRow({ baseUrl }: { baseUrl: string }) {
  const socialGutterWidth = 20

  const totalSocialGutterWidth = (socialLinks.length - 1) * socialGutterWidth
  const totalSocialIconWidth = socialLinks.length * socialIconSize
  const centeredColumnWidth = totalSocialGutterWidth + totalSocialIconWidth

  const outerGutterWidth = (fullPxWidth - centeredColumnWidth) / 2

  return (
    <Row className="mb-[48px]">
      <Gutter width={outerGutterWidth} />
      {socialLinks.map(({ name, href, imgPath }, i, arr) => {
        // Include icon gutter except for final icon
        const includeIconGutter = i < arr.length - 1
        return (
          <>
            <Column
              className={`h-[${socialIconSize}px] w-[${socialIconSize}px]`}
            >
              <SocialLink name={name} href={href} imgPath={baseUrl + imgPath} />
            </Column>
            {includeIconGutter && <Gutter width={socialGutterWidth} />}
          </>
        )
      })}
      <Gutter width={outerGutterWidth} />
    </Row>
  )
}

function EmailFooter({ baseUrl, t }: { baseUrl: string; t: TFunction }) {
  return (
    <>
      <Column className="mx-auto text-center">
        <SocialRow baseUrl={baseUrl} />
        <Row>
          <Gutter width={74} />
          <Column className="w-[452px] text-center">
            <FooterText>
              <span className="mb-[24px] block">
                &copy; {new Date().getFullYear()} {t('footer.about')}
              </span>
              <span className="mb-[24px] block">{physicalAddress}</span>

              <div>
                <div className="inline px-[10px]">
                  <Link href={baseUrl}>
                    {t('website')}
                    <span className="hidden">: </span>
                  </Link>
                </div>

                <div className="border-black-default inline border-x border-y-0 border-solid px-[13px]">
                  <Link href={`${baseUrl}/terms`}>
                    {t('termsAndConditions')}
                    <span className="hidden">: </span>
                  </Link>
                </div>

                <div className="inline px-[10px]">
                  <Link href={`${baseUrl}/privacy`}>
                    {t('privacyPolicy')}
                    <span className="hidden">: </span>
                  </Link>
                </div>
              </div>
            </FooterText>
          </Column>

          <Gutter width={74} />
        </Row>
      </Column>
    </>
  )
}

export function EmailTemplate({
  baseUrl,
  children,
  type,
  t,
}: {
  baseUrl: string
  children: ReactNode
  type: EmailType
  t: TFunction
}) {
  const baseImgUrl = baseUrl.includes('localhost')
    ? 'https://staging.sparklinglanguage.com'
    : baseUrl

  const logoImagePath = `${baseImgUrl}/img/email/logo.png`
  const backgroundImagePath = `${baseImgUrl}/img/email/background-${type}.png`
  const heroImagePath = `${baseImgUrl}/img/email/hero-${type}.jpg`

  const backgroundColors: Record<EmailType, string> = {
    'sign-up': 'linear-gradient(23deg, #EAF6FB 14.73%, #F3F9FC 85.27%)',
    'reset-password': 'hsla(270, 20%, 98%, 1)',
  }

  const bodyStyles = {
    fontFamily: '"Google Sans",Roboto,RobotoDraft,Helvetica,Arial,sans-serif',
    background: backgroundColors[type],
    backdropFilter: 'blur(2px)',
    width: '100%',
  }

  const containerStyles = {
    backgroundImage: `url(${backgroundImagePath})`,
    backgroundPosition: '50% 70px',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100%',
    maxWidth: fullPxWidth + 'px',
  }

  const mainStyles = {
    backgroundColor: '#ffffff',
    boxShadow:
      '0px 12px 32px -16px rgba(0, 0, 0, 0.05), 0px 8px 40px 0px rgba(0, 0, 61, 0.05)',
  }

  return (
    <Tailwind>
      <Html lang="en" dir="ltr">
        <Body style={bodyStyles}>
          <Container style={containerStyles} className={`w-[${fullPxWidth}px]`}>
            <Row className="mt-[32px] mb-[64px] w-[552px]">
              <Column className="w-auto" />
              <Column className="mt-[32px]">
                <Img
                  className="mx-auto"
                  alt="SparkLing"
                  src={logoImagePath}
                  width="190"
                  height="32"
                />
              </Column>
              <Column className="w-auto" />
            </Row>

            <Row
              style={mainStyles}
              className="mb-[64px] w-[552px] rounded-[16px]"
            >
              <Column className="rounded-t-[16px]">
                <Row className="rounded-t-[16px]">
                  <Column className="rounded-t-[16px]">
                    <Img className="rounded-t-[16px]" src={heroImagePath} />
                  </Column>
                </Row>
                <Row>
                  <Gutter width={48} />
                  <Column>{children}</Column>
                  <Gutter width={48} />
                </Row>
              </Column>
            </Row>
            <Row>
              <EmailFooter baseUrl={baseImgUrl} t={t} />
            </Row>
          </Container>
        </Body>
      </Html>
    </Tailwind>
  )
}
